import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import GameCover from '../tavern/GameCover';
import { getInitials } from '../companions/presence';
import { gameTitle } from '../games/gameFormat';
import {
  formatDisplayDate,
  formatDisplayTime,
  timeInputValueFromDateTime,
} from '../../i18n/formatters';

const textWrapSx = {
  minWidth: 0,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const hasValidGameId = (game) => {
  const id = Number(game?.id);

  return Number.isInteger(id) && id > 0;
};

const validGameOptions = (games = []) =>
  (Array.isArray(games) ? games : []).filter(hasValidGameId);

const validPositiveId = (value) => {
  const id = Number(value);

  return Number.isInteger(id) && id > 0 ? id : null;
};

const groupedAssignments = (assignments, t) => {
  const groups = new Map();

  (Array.isArray(assignments) ? assignments : []).forEach((assignment) => {
    if (!hasValidGameId(assignment?.game)) return;

    const bringer = assignment.bringer || null;
    const key = bringer?.id ? `person-${bringer.id}` : 'unknown';

    if (!groups.has(key)) {
      groups.set(key, {
        bringer,
        name: bringer?.displayName || t('common.unknown'),
        games: [],
      });
    }

    groups.get(key).games.push(assignment.game);
  });

  return Array.from(groups.values()).filter((group) => group.games.length > 0);
};

export const NightContributionsPanel = ({
  assignments = [],
  compact = false,
  emptyLabel,
  language,
  t,
}) => {
  const groups = groupedAssignments(assignments, t);
  const coverSize = compact ? 42 : 52;

  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Stack spacing={compact ? 1 : 1.25} sx={{ minWidth: 0 }}>
      {groups.map((group) => (
        <Box
          key={group.bringer?.id || group.name}
          sx={{
            display: 'grid',
            gridTemplateColumns: compact
              ? { xs: '1fr', sm: 'minmax(128px, 0.65fr) minmax(0, 1fr)' }
              : { xs: '1fr', sm: 'minmax(148px, 0.6fr) minmax(0, 1fr)' },
            gap: compact ? 1 : 1.25,
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            p: compact ? 1.25 : 1.5,
            border: '1px solid',
            borderColor: 'rgba(15, 76, 69, 0.16)',
            borderRadius: 2,
            bgcolor: 'rgba(236, 170, 65, 0.08)',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
              src={group.bringer?.avatarUrl || undefined}
              alt={group.name}
              sx={{
                width: compact ? 32 : 36,
                height: compact ? 32 : 36,
                flex: '0 0 auto',
                bgcolor: 'primary.dark',
                fontSize: compact ? '0.75rem' : '0.82rem',
              }}
            >
              {getInitials(group.name)}
            </Avatar>
            <Box sx={{ flex: '1 1 auto', ...textWrapSx }}>
              <Typography variant={compact ? 'body2' : 'h4'} fontWeight={900} sx={textWrapSx}>
                {group.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={textWrapSx}>
                {group.games.map((game) => gameTitle(game, t, language)).join(', ')}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={0.75}
            useFlexGap
            sx={{
              minWidth: 0,
              flexWrap: 'wrap',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
            }}
          >
            {group.games.map((game) => (
              <Box
                key={game.id}
                sx={{
                  width: coverSize,
                  minWidth: coverSize,
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  boxShadow: '0 10px 22px -18px rgba(6, 37, 34, 0.72)',
                }}
              >
                <GameCover
                  game={game}
                  size="small"
                  showBggRating={false}
                  sx={{ width: coverSize }}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

export const NightVotesPanel = ({
  compact = false,
  language,
  limit = 4,
  night,
  showTiming = true,
  t,
}) => {
  const voting = night?.voting || null;
  const selectedGameIds = new Set(
    (Array.isArray(voting?.viewerGameIds) ? voting.viewerGameIds : [])
      .map(Number)
      .filter((id) => Number.isInteger(id) && id > 0)
  );
  const availableGames = validGameOptions(night?.availableGames);
  const storedGames = validGameOptions(night?.games);
  const candidateGames = availableGames.length ? availableGames : storedGames;
  const gameById = new Map(candidateGames.map((game) => [Number(game.id), game]));

  if (!voting || candidateGames.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
        {t('nights.votes.noVotesYet')}
      </Typography>
    );
  }

  const visibleResults = (Array.isArray(voting.results) ? voting.results : [])
    .map((result) => {
      const gameId = validPositiveId(result?.gameId);
      if (!gameId) return null;

      return {
        ...result,
        gameId,
        game: gameById.get(gameId),
        votes: Number(result?.votes) || 0,
        votedByViewer: selectedGameIds.has(gameId) || Boolean(result?.votedByViewer),
      };
    })
    .filter(Boolean)
    .filter((result) => result.game && (result.votes > 0 || result.votedByViewer))
    .sort(
      (left, right) =>
        right.votes - left.votes ||
        gameTitle(left.game, t, language).localeCompare(
          gameTitle(right.game, t, language),
          language,
          { sensitivity: 'base' }
        )
    )
    .slice(0, limit);

  return (
    <Box sx={{ minWidth: 0 }}>
      {showTiming ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, ...textWrapSx }}>
          {voting.open
            ? t('nights.votes.openUntil', {
                date: formatDisplayDate(voting.closesAt, language),
                time: formatDisplayTime(timeInputValueFromDateTime(voting.closesAt), language),
              })
            : t('nights.votes.lockedTitle')}
        </Typography>
      ) : null}

      {visibleResults.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={textWrapSx}>
          {t('nights.votes.noVotesYet')}
        </Typography>
      ) : (
        <Stack spacing={compact ? 0.85 : 1}>
          {visibleResults.map((result, index) => {
            const ownerName = result.game.ownerName || result.game.owners?.[0]?.displayName;
            const ownerAvatar = result.game.ownerAvatarUrl || result.game.owners?.[0]?.avatarUrl;

            return (
              <Card
                key={result.gameId}
                variant="outlined"
                sx={{
                  bgcolor: 'rgba(15, 76, 69, 0.045)',
                  borderColor: 'rgba(15, 76, 69, 0.16)',
                }}
              >
                <CardContent sx={{ p: compact ? 1 : 1.25, '&:last-child': { pb: compact ? 1 : 1.25 } }}>
                  <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
                    <Chip
                      label={`#${index + 1}`}
                      size="small"
                      sx={{
                        flex: '0 0 auto',
                        minWidth: 38,
                        bgcolor: 'primary.dark',
                        color: 'primary.contrastText',
                        fontWeight: 900,
                      }}
                    />
                    <GameCover
                      game={result.game}
                      size="small"
                      showBggRating={false}
                      sx={{ width: compact ? 42 : 48, flex: '0 0 auto' }}
                    />
                    <Box sx={{ flex: '1 1 auto', ...textWrapSx }}>
                      <Typography variant="body2" fontWeight={900} sx={textWrapSx}>
                        {gameTitle(result.game, t, language)}
                      </Typography>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25, minWidth: 0 }}>
                        <Avatar
                          src={ownerAvatar || undefined}
                          alt={ownerName || t('common.unknown')}
                          sx={{ width: 20, height: 20, fontSize: '0.66rem', flex: '0 0 auto' }}
                        >
                          {getInitials(ownerName || t('common.unknown'))}
                        </Avatar>
                        <Typography variant="caption" color="text.secondary" sx={textWrapSx}>
                          {ownerName || t('common.unknown')}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack spacing={0.35} alignItems="flex-end" sx={{ flex: '0 0 auto' }}>
                      <Typography variant="body2" fontWeight={900}>
                        {t('nights.votes.count', { count: result.votes })}
                      </Typography>
                      {result.votedByViewer ? (
                        <Typography
                          variant="caption"
                          color="secondary.dark"
                          fontWeight={900}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {t('nights.votes.yourVote')}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};
